import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, UploadSimple, Trash, Eye, EyeSlash, Download, ArrowSquareOut, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import API from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { openPdf } from "./ResellerDetailPage";

const EMPTY = { kind: "link", title: "", description: "", category: "link", url: "", subject: "", body: "", sort: 50 };

function ResourceForm({ form, setForm, categories, placeholders, editing }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="space-y-3">
      {!editing && (
        <div className="grid grid-cols-3 gap-2" role="radiogroup">
          {[["link", "Link"], ["template", "E-mail template"], ["file", "File upload"]].map(([k, l]) => (
            <button key={k} type="button" role="radio" aria-checked={form.kind === k} onClick={() => setForm({ ...form, kind: k, category: k === "template" ? "template" : k === "file" ? "download" : form.category })}
              className={`rounded-lg border px-3 py-2 text-sm text-left ${form.kind === k ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"}`} data-testid={`resource-kind-${k}`}>{l}</button>
          ))}
        </div>
      )}
      <div><Label className="text-xs">Title *</Label><Input value={form.title} onChange={set("title")} className="mt-1" data-testid="resource-title-input" /></div>
      <div><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={set("description")} rows={2} className="mt-1" data-testid="resource-description-input" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Section</Label>
          <select value={form.category} onChange={set("category")} className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="resource-category-select">
            {Object.entries(categories).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div><Label className="text-xs">Order</Label><Input type="number" value={form.sort} onChange={set("sort")} className="mt-1" data-testid="resource-sort-input" /></div>
      </div>
      {form.kind === "link" && <div><Label className="text-xs">URL *</Label><Input value={form.url} onChange={set("url")} placeholder="https://" className="mt-1" data-testid="resource-url-input" /></div>}
      {form.kind === "template" && (
        <>
          <div><Label className="text-xs">Subject</Label><Input value={form.subject} onChange={set("subject")} className="mt-1" data-testid="resource-subject-input" /></div>
          <div><Label className="text-xs">Body *</Label><Textarea value={form.body} onChange={set("body")} rows={8} className="mt-1 font-mono text-xs" data-testid="resource-body-input" /></div>
          <p className="text-[10px] text-muted-foreground">Placeholders filled per reseller: {placeholders.map((p) => `{{${p}}}`).join(" ")}</p>
        </>
      )}
      {form.kind === "file" && !editing && <div><Label className="text-xs">File * (PDF, image, deck… max 25 MB)</Label><Input type="file" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} className="mt-1" data-testid="resource-file-input" /></div>}
    </div>
  );
}

export default function ResellerResourcesPage() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await API.get("/platform/resellers/resources"); setData(data); } catch { toast.error("Couldn't load resources"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      if (form.id) {
        const { title, description, category, sort, url, subject, body } = form;
        await API.put(`/platform/resellers/resources/${form.id}`, { title, description, category, sort, ...(form.kind === "link" ? { url } : {}), ...(form.kind === "template" ? { subject, body } : {}) });
      } else if (form.kind === "file") {
        if (!form.file) { toast.error("Choose a file"); setBusy(false); return; }
        const fd = new FormData();
        fd.append("file", form.file);
        await API.post(`/platform/resellers/resources/upload?title=${encodeURIComponent(form.title)}&description=${encodeURIComponent(form.description || "")}&category=${encodeURIComponent(form.category)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        await API.post("/platform/resellers/resources", form);
      }
      toast.success(form.id ? "Resource updated" : "Resource added");
      setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
    setBusy(false);
  };
  const toggle = async (r) => {
    try { await API.put(`/platform/resellers/resources/${r.id}`, { active: !r.active }); toast.success(r.active ? "Hidden from resellers" : "Visible to resellers"); load(); } catch { toast.error("Couldn't update"); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Remove "${r.title}"?`)) return;
    try { const { data: d } = await API.delete(`/platform/resellers/resources/${r.id}`); toast.success(d.message || "Removed"); load(); } catch { toast.error("Couldn't remove"); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5" data-testid="reseller-resources-admin">
      <Link to="/platform/resellers" className="text-xs text-muted-foreground hover:underline inline-flex items-center gap-1"><ArrowLeft size={12} /> Resellers</Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold font-[Chivo]">Reseller resources hub</h1>
          <p className="text-sm text-muted-foreground">What resellers find under <b>Resources</b> in their portal. Templates are filled with each reseller's code, link and contact details when they open them.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setForm({ ...EMPTY, kind: "file", category: "download" })} data-testid="resource-upload-btn"><UploadSimple size={14} className="mr-1" /> Upload file</Button>
          <Button size="sm" onClick={() => setForm({ ...EMPTY })} data-testid="resource-add-btn"><Plus size={14} className="mr-1" /> Add link or template</Button>
        </div>
      </div>

      {!data ? <p className="text-sm text-muted-foreground">Loading…</p> : (
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground"><tr><th className="text-left px-4 py-2">Resource</th><th className="text-left px-4 py-2">Section</th><th className="text-left px-4 py-2">Type</th><th className="text-left px-4 py-2">Visible</th><th /></tr></thead>
            <tbody>
              {data.resources.map((r) => (
                <tr key={r.id} className={`border-t border-border ${r.active ? "" : "opacity-60"}`} data-testid={`resource-row-${r.id}`}>
                  <td className="px-4 py-2"><p className="font-medium">{r.title}</p><p className="text-[11px] text-muted-foreground line-clamp-1">{r.description}</p>{r.seed_key && <Badge variant="secondary" className="text-[9px] mt-0.5">Starter</Badge>}</td>
                  <td className="px-4 py-2 text-xs">{r.category_label}</td>
                  <td className="px-4 py-2 text-xs capitalize">{r.kind === "generated" ? "Generated PDF" : r.kind}</td>
                  <td className="px-4 py-2">{r.active ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Visible</Badge> : <Badge variant="secondary" className="text-[10px]">Hidden</Badge>}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {r.kind === "link" && <a href={r.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center h-8 px-2 text-xs hover:underline" data-testid={`resource-open-${r.id}`}><ArrowSquareOut size={13} /></a>}
                    {(r.kind === "file" || r.kind === "generated") && <Button variant="ghost" size="sm" onClick={() => openPdf(r.admin_download_path)} data-testid={`resource-download-${r.id}`}><Download size={13} /></Button>}
                    <Button variant="ghost" size="sm" onClick={() => setForm({ ...r, url: r.raw_url ?? r.url, sort: r.sort ?? 50 })} data-testid={`resource-edit-${r.id}`}><PencilSimple size={13} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => toggle(r)} data-testid={`resource-toggle-${r.id}`}>{r.active ? <EyeSlash size={13} /> : <Eye size={13} />}</Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(r)} data-testid={`resource-delete-${r.id}`}><Trash size={13} /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(o) => { if (!o) setForm(null); }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="resource-dialog">
          <DialogHeader><DialogTitle>{form?.id ? "Edit resource" : form?.kind === "file" ? "Upload a file" : "Add a resource"}</DialogTitle><DialogDescription>Shown to every reseller in their portal.</DialogDescription></DialogHeader>
          {form && data && <ResourceForm form={form} setForm={setForm} categories={data.categories} placeholders={data.placeholders} editing={!!form.id} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Cancel</Button>
            <Button onClick={save} disabled={busy || !form?.title} data-testid="resource-save">{busy ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
